import { useState, useEffect, useCallback } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import BlockIcon from '@mui/icons-material/Block';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { listIssuances, revokeIssuance, IssuanceStatus, IssuerIssuance } from '../api/rewards';
import BulkIssueDialog from '../components/BulkIssueDialog';

const PAGE_SIZE = 25;

function formatDate(d: string | null | undefined): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function SourceChip({ row }: { row: IssuerIssuance }) {
    if (row.isDeleted) return <Chip size="small" label="Revoked" color="error" variant="outlined" />;
    if (row.isManual)  return <Chip size="small" label="Manual"  color="info"    variant="outlined" />;
    return <Chip size="small" label="Earned" color="success" variant="outlined" />;
}

export default function IssuedBadgesPage() {
    const [status, setStatus]   = useState<IssuanceStatus>('active');
    const [rows, setRows]       = useState<IssuerIssuance[]>([]);
    const [total, setTotal]     = useState(0);
    const [page, setPage]       = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState('');
    const [confirmRevoke, setConfirmRevoke] = useState<IssuerIssuance | null>(null);
    const [bulkOpen, setBulkOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const res = await listIssuances({ status, page, pageSize: PAGE_SIZE });
        if (res.success) {
            setRows(res.rows);
            setTotal(res.total);
        } else {
            setError(res.message ?? 'Failed to load issuances.');
            setRows([]);
            setTotal(0);
        }
        setLoading(false);
    }, [status, page]);

    useEffect(() => { void load(); }, [load]);

    const handleStatusChange = (next: IssuanceStatus) => {
        setStatus(next);
        setPage(1);
    };

    const handleRevoke = async () => {
        if (!confirmRevoke) return;
        const id = confirmRevoke.issuedBadgeID;
        setConfirmRevoke(null);
        const res = await revokeIssuance(id);
        if (res.success) {
            setSnackbar('Issuance revoked.');
            void load();
        } else {
            setSnackbar(res.message ?? 'Failed to revoke issuance.');
        }
    };

    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>Issued Badges</Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                        Every badge handed out across the issuers you manage.
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="status-label">Status</InputLabel>
                        <Select
                            labelId="status-label"
                            label="Status"
                            value={status}
                            onChange={(e) => handleStatusChange(e.target.value as IssuanceStatus)}
                        >
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="revoked">Revoked</MenuItem>
                            <MenuItem value="all">All</MenuItem>
                        </Select>
                    </FormControl>
                    <Button variant="contained" startIcon={<GroupAddIcon />} onClick={() => setBulkOpen(true)}>
                        Bulk issue
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                            <TableCell>Badge</TableCell>
                            <TableCell>Recipient</TableCell>
                            <TableCell>Issuer</TableCell>
                            <TableCell>Issued</TableCell>
                            <TableCell>Expires</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            Array.from({ length: 5 }).map((_, i) => (
                                <TableRow key={`s-${i}`}>
                                    <TableCell colSpan={7}><Skeleton height={32} /></TableCell>
                                </TableRow>
                            ))
                        )}
                        {!loading && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Box py={4} textAlign="center" color="text.secondary">
                                        {status === 'active'
                                            ? "You haven't issued any badges yet."
                                            : status === 'revoked'
                                                ? 'No revoked issuances.'
                                                : 'No issuances to show.'}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && rows.map((row) => (
                            <TableRow key={row.issuedBadgeID} hover>
                                <TableCell>
                                    <Box display="flex" alignItems="center" gap={1.5}>
                                        <Avatar
                                            src={row.badgeImage ?? undefined}
                                            alt={row.badgeName}
                                            variant="rounded"
                                            sx={{ width: 36, height: 36, bgcolor: 'action.hover' }}
                                        >
                                            {!row.badgeImage && <WorkspacePremiumIcon fontSize="small" />}
                                        </Avatar>
                                        <Box minWidth={0}>
                                            <Typography variant="body2" fontWeight={500} noWrap>{row.badgeName}</Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box minWidth={0}>
                                        {row.recipientName && (
                                            <Typography variant="body2" fontWeight={500} noWrap>{row.recipientName}</Typography>
                                        )}
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            {row.recipientEmail || '—'}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary" noWrap>{row.issuerName ?? '—'}</Typography>
                                </TableCell>
                                <TableCell>{formatDate(row.issuedDate)}</TableCell>
                                <TableCell>{formatDate(row.expiryDate)}</TableCell>
                                <TableCell><SourceChip row={row} /></TableCell>
                                <TableCell align="right">
                                    {!row.isDeleted && (
                                        <Tooltip title="Revoke">
                                            <IconButton
                                                size="small"
                                                onClick={() => setConfirmRevoke(row)}
                                                aria-label="Revoke issuance"
                                            >
                                                <BlockIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>

            {pageCount > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination
                        count={pageCount}
                        page={page}
                        onChange={(_, p) => setPage(p)}
                        color="primary"
                    />
                </Box>
            )}

            <Dialog open={Boolean(confirmRevoke)} onClose={() => setConfirmRevoke(null)}>
                <DialogTitle>Revoke this badge?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will revoke <strong>{confirmRevoke?.badgeName}</strong> issued to{' '}
                        <strong>{confirmRevoke?.recipientEmail || 'this recipient'}</strong>.
                        The recipient will no longer see it in their portfolio.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => setConfirmRevoke(null)}>Cancel</Button>
                    <Button onClick={handleRevoke} color="error">Revoke</Button>
                </DialogActions>
            </Dialog>

            <BulkIssueDialog
                open={bulkOpen}
                onClose={() => setBulkOpen(false)}
                onIssued={() => { setSnackbar('Bulk issuance complete.'); void load(); }}
            />

            <Snackbar
                open={Boolean(snackbar)}
                autoHideDuration={4000}
                onClose={() => setSnackbar('')}
                message={snackbar}
            />
        </Container>
    );
}
